import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function TrailerIUdlandet() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Hastighed og kørsel", path: "/trailer-hastighed" },
    { name: "Trailer i udlandet", path: "/trailer-i-udlandet" }
  ];
  const tocItems = [
    { id: "hastighedsoversigt-europa", title: "Hastighedsgrænser med trailer i Europa" },
    { id: "norge", title: "Særligt om Norge og Tempo 100" },
    { id: "frankrig-3500-kg", title: "Særligt om Frankrig og 3.500 kg-fælden" },
    { id: "oestrig-og-italien", title: "Østrig, Schweiz og Italien" },
    { id: "dokumenter-udstyr", title: "Obligatoriske dokumenter og udstyr på europæiske veje" }
  ];

  const faq = [
    {
      q: "Gælder en dansk Tempo 100-godkendelse i Norge?",
      a: "Ja, Norge og Danmark har en bilateral anerkendelsesaftale. En trailer med dansk Tempo 100-mærke må køre op til 100 km/t på de dertil skiltede norske motorvejsstrækninger."
    },
    {
      q: "Hvorfor skal man passe på med hastigheden i Frankrig med trailer?",
      a: "I Frankrig må man køre op til 130 km/t med trailer, hvis vogntogets samlede tilladte totalvægt er højst 3.500 kg. Men hvis vogntogets tilladte totalvægt overstiger 3.500 kg, falder grænsen drastisk til 90 km/t på motorvej og 80 km/t på landevej, og man skal have hastighedsmærkater (80 og 90) bagpå."
    },
    {
      q: "Hvilke papirer skal man altid have med på traileren i udlandet?",
      a: "Du skal altid medbringe original del 1 af registreringsattesten for både bil og trailer. Traileren skal desuden have enten en EU-nummerplade med DK-nationalitetsmærke eller et fysisk hvidt DK-ovalmærke bagpå."
    },
    {
      q: "Skal man betale motorvejsafgift (vignette) for traileren i Schweiz og Østrig?",
      a: "I Schweiz skal du købe to separate vignetter (en til bilen og en til traileren). I Østrig dækker bilens motorvejsvignette normalt også påhængsvogne under 3.500 kg."
    }
  ];

  const sources = [
    { title: "Færdselsstyrelsen: Krav og regler for Tempo 100 påhængskøretøjer i Danmark og udlandet", source: "fstyr.dk", url: "https://fstyr.dk/privat/krav-til-koeretoejer/tempo-100" },
    { title: "Færdselsstyrelsen: Kørekort og kørsel i udlandet", source: "fstyr.dk", url: "https://fstyr.dk/privat/koerekort/koerekort-og-udland" },
    { title: "Trafikförordning (1998:1276) 4 kap. 20 §", source: "riksdagen.se", url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/trafikforordning-19981276_sfs-1998-1276/" }
  ];

  return (
    <>
      <SEO 
        title="Trailer i udlandet: Hastigheder i Europa og regler" 
        description="Få overblik over kørsel med trailer i Norge, Frankrig, Østrig, Italien og resten af Europa: fartgrænser, 3.500 kg-regler og nødvendige dokumenter." 
        pathname="/trailer-i-udlandet"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Trailer i udlandet: Hastighedsgrænser i Europa
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          Hastighedsgrænser for personbiler med trailer varierer markant på tværs af de europæiske landegrænser: Fra <strong>80 km/t</strong> i Norge, Sverige og Tyskland op til <strong>130 km/t</strong> i Frankrig (for lette vogntog).
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          I Norge anerkendes den danske Tempo 100-godkendelse direkte, mens Tyskland kræver sin egen tyske mærkat. I Frankrig afhænger fartgrænsen af, om vogntogets samlede tilladte totalvægt overstiger 3.500 kg.
        </p>

        <TableOfContents items={tocItems} />

        <h2 id="hastighedsoversigt-europa" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hastighedsgrænser med trailer i Europa
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Nedenstående tabel viser de generelle fartgrænser for standardvogntog (personbil med bremset trailer op til 3.500 kg samlet vægt):
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Land</th>
                <th className="py-2.5 pr-4 font-semibold">Byzone</th>
                <th className="py-2.5 pr-4 font-semibold">Landevej</th>
                <th className="py-2.5 pr-4 font-semibold">Motorvej</th>
                <th className="py-2.5 font-semibold">Særlige vilkår</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Danmark</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 text-[#5C636B]">100 km/t med dansk Tempo 100</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Tyskland</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 text-[#5C636B]">100 km/t med tysk Tempo 100. 60 km/t på landevej hvis &gt; 3,5t</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Sverige</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 text-[#5C636B]">40 km/t for visse ubremsede trailere</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Norge</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 text-[#5C636B]">100 km/t på motorvej med dansk Tempo 100</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Frankrig (&le; 3,5 t)</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">130 km/t</td>
                <td className="py-3 text-[#5C636B]">110 km/t ved regnvejr. Kræver stor agtpågivenhed</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Frankrig (&gt; 3,5 t)</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">90 km/t</td>
                <td className="py-3 text-[#5C636B]">Kræver 80- og 90-mærkater bagpå vogntoget</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Østrig</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4">80 / 100 km/t</td>
                <td className="py-3 text-[#5C636B]">100 km/t kun for trailer &le; 750 kg; ellers 80 km/t</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Italien</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">70 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 text-[#5C636B]">Fast grænse på 80 km/t på alle motorveje</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Holland</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4">90 km/t</td>
                <td className="py-3 text-[#5C636B]">90 km/t på motorvej for trailer op til 3.500 kg</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 id="norge" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Særligt om Norge og Tempo 100
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Statens vegvesen i Norge indførte i 2022 opdaterede regler for tilhengere. Hovedreglen er <strong>80 km/t på landeveje og motorveje</strong>.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Har din trailer dansk Tempo 100, anerkendes denne fuldt ud i Norge. Du må derfor køre op til 100 km/t på motorvejsstrækninger skiltet med 100 eller 110 km/t. Læs kravene til godkendelsen under <Link to="/tempo-100" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">Tempo 100</Link>.
        </p>

        <h2 id="frankrig-3500-kg" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Særligt om Frankrig og 3.500 kg-fælden
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I Frankrig (Code de la Route art. R413-8) beregnes grænsen ud fra vogntogets <em>tilladte totalvægt</em> (PTRA angivet på bilens typeattest):
        </p>

        <div className="my-6 p-4 border border-[#E4E1D8] bg-[#FBFAF6] rounded-[4px] space-y-3">
          <p className="text-[16px] text-[#1A1C1F] m-0">
            Hvis summen af bilens tilladte totalvægt og trailerens tilladte totalvægt overstiger <strong>3.500 kg</strong>, må du aldrig køre hurtigere end <strong>90 km/t på motorvej</strong> og <strong>80 km/t på landevej</strong>.
          </p>
          <p className="text-[15px] text-[#5C636B] m-0">
            Desuden skal køretøjet ifølge fransk lov have to runde hastighedsmærkater (et med "80" og et med "90") monteret bagpå traileren. Bøden for overtrædelse er betydelig.
          </p>
        </div>

        <h2 id="oestrig-og-italien" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Østrig, Schweiz og Italien
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når du kører gennem Alperne mod Sydeuropa:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Schweiz:</strong> Maksimalt 80 km/t på motorvej (eller 100 km/t for lette trailere op til 3.500 kg med specifik godkendelse). Kræver 2 motorvejsvignetter (en til bil og en til trailer).</li>
          <li><strong>Østrig:</strong> Trailere over 750 kg må maksimalt køre 80 km/t på Autobahn og 100 km/t på motortrafikveje. Kun ubremsede trailere under 750 kg må køre 100 km/t på Autobahn.</li>
          <li><strong>Italien:</strong> Den absolutte grænse for alle vogntog er 80 km/t på motorvej og 70 km/t på landevej. Bøder udstedes flittigt via fartkontrolsystemet Tutor.</li>
        </ul>

        <h2 id="dokumenter-udstyr" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Obligatoriske dokumenter og udstyr på europæiske veje
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når du tager traileren med til udlandet, skal du have styr på følgende:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Registreringsattest:</strong> Original del 1 for både trækbil og trailer.</li>
          <li><strong>Nationalitetsmærke:</strong> Hvis traileren ikke har en EU-nummerplade (med blåt DK-bånd), skal der monteres et ovalt, hvidt DK-klistermærke bagpå.</li>
          <li><strong>Forsikringspapirer:</strong> Traileren er ansvarsforsikret gennem trækbilen i hele EU/EØS, men medbring det grønne kort, hvis du kører uden for EU. Se reglerne under <Link to="/trailer-forsikring" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailerforsikring</Link>.</li>
          <li><strong>Sikkerhedsudstyr:</strong> Advarselstrekant, godkendte refleksveste og forbindskasse.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs også de specifikke regler for vore to nabolande: <Link to="/trailer-i-tyskland" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">Trailer i Tyskland</Link> og <Link to="/trailer-i-sverige" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">Trailer i Sverige</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
